/**
 * Quiet Art Movement - Shipping & Volumetric Calculator
 * Handles packaging dimension rules, volumetric weight calculations,
 * and comprehensive Blue Dart / Air Express freight calculations.
 */

export type ShippingMode = 'Air' | 'Surface' | 'DP';

export type DestinationRegion = 'NORTH' | 'WEST_CENTRAL' | 'EAST' | 'SOUTH' | 'NE' | 'JK';

export interface ArtworkPackagingInput {
  artworkWidth: number; // in inches
  artworkHeight: number; // in inches
  artworkDepth?: number; // in inches (for sculptures / 3D pieces)
  hasFrame?: boolean;
  selectedFrameDepth?: number | null; // in inches
  actualWeightKg?: number;
  declaredValueInr?: number;
}

export interface PackageDimensions {
  packageWidthInches: number; // artworkWidth + 4
  packageHeightInches: number; // artworkHeight + 4
  packageDepthInches: number; // 8 (non-frame) or 8 + frameDepth (frame)
  packageWidthCm: number;
  packageHeightCm: number;
  packageDepthCm: number;
  airVolumetricWeightKg: number; // (L * W * H in cm) / 5000
  surfaceVolumetricWeightKg: number; // (L * W * H in cm) / 4500
  isOverSize: boolean; // any dimension > 120cm (~47.24 inches)
}

export interface ShippingCostOptions {
  mode?: ShippingMode;
  destinationZone?: DestinationRegion;
  destinationState?: string;
  isDelhi?: boolean;
  isMaharashtra?: boolean;
  isRemoteArea?: boolean;
  fuelSurchargePercent?: number; // default: 81.60%
  cafPercent?: number; // default: 17.50%
  idcPercent?: number; // default: 5.0%
  docketFee?: number; // default: 70
}

export interface ShippingCostBreakdown {
  ratePerKg: number;
  minChargeableWeightKg: number;
  minBaseFreight: number;
  baseFreight: number;
  docketFee: number;
  fovFee: number; // Freight on Value / Owner's Risk
  osOwFee: number; // Over Size / Over Weight surcharge
  eccFee: number; // Environment Compensation Charge (Delhi)
  specialHandlingFee: number; // Special handling (Maharashtra)
  remoteAreaSurcharge: number; // RAS (Bihar, Jharkhand, Kerala, J&K, Ladakh)
  vchcFee: number; // Valuable Cargo Handling Charge
  subtotal: number;
  fuelSurcharge: number;
  caf: number;
  idc: number;
}

export interface ShippingCalculationResult {
  packageDimensions: PackageDimensions;
  actualWeightKg: number;
  volumetricWeightKg: number;
  chargeableWeightKg: number; // max(actualWeight, volumetricWeight)
  billableWeightKg: number; // max(chargeableWeight, minWeight)
  breakdown: ShippingCostBreakdown;
  totalShippingCost: number; // Subtotal + FS + CAF + IDC
}

/**
 * State to Region & Surcharge mapping
 */
export const STATE_REGION_MAP: Record<string, DestinationRegion> = {
  // North
  hp: 'NORTH',
  'himachal pradesh': 'NORTH',
  punjab: 'NORTH',
  haryana: 'NORTH',
  uttarakhand: 'NORTH',
  up: 'NORTH',
  'uttar pradesh': 'NORTH',
  rajasthan: 'NORTH',
  delhi: 'NORTH',
  'new delhi': 'NORTH',
  chandigarh: 'NORTH',

  // East
  bihar: 'EAST',
  orissa: 'EAST',
  odisha: 'EAST',
  'west bengal': 'EAST',
  wb: 'EAST',
  jharkhand: 'EAST',

  // West & Central
  maharashtra: 'WEST_CENTRAL',
  mp: 'WEST_CENTRAL',
  'madhya pradesh': 'WEST_CENTRAL',
  gujarat: 'WEST_CENTRAL',
  chhattisgarh: 'WEST_CENTRAL',
  goa: 'WEST_CENTRAL',
  'diu & daman': 'WEST_CENTRAL',
  'daman and diu': 'WEST_CENTRAL',

  // South
  karnataka: 'SOUTH',
  'tamil nadu': 'SOUTH',
  kerala: 'SOUTH',
  'andhra pradesh': 'SOUTH',
  ap: 'SOUTH',
  telangana: 'SOUTH',
  pondicherry: 'SOUTH',
  puducherry: 'SOUTH',

  // North East
  nagaland: 'NE',
  mizoram: 'NE',
  manipur: 'NE',
  meghalaya: 'NE',
  'arunachal pradesh': 'NE',
  tripura: 'NE',
  sikkim: 'NE',
  assam: 'NE',

  // J&K
  jammu: 'JK',
  kashmir: 'JK',
  'jammu & kashmir': 'JK',
  'jammu and kashmir': 'JK',
  ladakh: 'JK',
};

/**
 * RAS States: Bihar, Jharkhand, Kerala, Jammu, Kashmir & Ladakh
 */
export const RAS_STATES = new Set([
  'bihar',
  'jharkhand',
  'kerala',
  'jammu',
  'kashmir',
  'jammu & kashmir',
  'jammu and kashmir',
  'ladakh',
]);

/**
 * Pincode prefix (first 2 digits) to valid State names/aliases
 */
export const PINCODE_PREFIX_STATE_MAP: Record<string, string[]> = {
  '11': ['Delhi', 'New Delhi'],
  '12': ['Haryana'],
  '13': ['Haryana'],
  '14': ['Punjab'],
  '15': ['Punjab'],
  '16': ['Chandigarh', 'Punjab'],
  '17': ['Himachal Pradesh'],
  '18': ['Jammu & Kashmir', 'Jammu and Kashmir', 'Ladakh'],
  '19': ['Jammu & Kashmir', 'Jammu and Kashmir', 'Ladakh'],
  '20': ['Uttar Pradesh'],
  '21': ['Uttar Pradesh'],
  '22': ['Uttar Pradesh'],
  '23': ['Uttar Pradesh'],
  '24': ['Uttar Pradesh', 'Uttarakhand'],
  '25': ['Uttar Pradesh'],
  '26': ['Uttar Pradesh', 'Uttarakhand'],
  '27': ['Uttar Pradesh'],
  '28': ['Uttar Pradesh'],
  '30': ['Rajasthan'],
  '31': ['Rajasthan'],
  '32': ['Rajasthan'],
  '33': ['Rajasthan'],
  '34': ['Rajasthan'],
  '36': ['Gujarat', 'Diu & Daman', 'Daman and Diu'],
  '37': ['Gujarat'],
  '38': ['Gujarat'],
  '39': ['Gujarat', 'Dadra and Nagar Haveli and Daman and Diu', 'Daman and Diu', 'Dadra & Nagar Haveli'],
  '40': ['Maharashtra', 'Goa'],
  '41': ['Maharashtra'],
  '42': ['Maharashtra'],
  '43': ['Maharashtra'],
  '44': ['Maharashtra'],
  '45': ['Madhya Pradesh'],
  '46': ['Madhya Pradesh'],
  '47': ['Madhya Pradesh'],
  '48': ['Madhya Pradesh'],
  '49': ['Chhattisgarh'],
  '50': ['Telangana', 'Andhra Pradesh'],
  '51': ['Andhra Pradesh'],
  '52': ['Andhra Pradesh'],
  '53': ['Andhra Pradesh'],
  '56': ['Karnataka'],
  '57': ['Karnataka'],
  '58': ['Karnataka'],
  '59': ['Karnataka'],
  '60': ['Tamil Nadu', 'Puducherry', 'Pondicherry'],
  '61': ['Tamil Nadu', 'Puducherry', 'Pondicherry'],
  '62': ['Tamil Nadu'],
  '63': ['Tamil Nadu'],
  '64': ['Tamil Nadu'],
  '67': ['Kerala', 'Lakshadweep'],
  '68': ['Kerala', 'Lakshadweep'],
  '69': ['Kerala', 'Lakshadweep'],
  '70': ['West Bengal'],
  '71': ['West Bengal'],
  '72': ['West Bengal'],
  '73': ['West Bengal'],
  '74': ['West Bengal', 'Andaman and Nicobar Islands', 'Andaman & Nicobar Islands'],
  '75': ['Odisha', 'Orissa'],
  '76': ['Odisha', 'Orissa'],
  '77': ['Odisha', 'Orissa'],
  '78': ['Assam'],
  '79': ['Arunachal Pradesh', 'Manipur', 'Meghalaya', 'Mizoram', 'Nagaland', 'Tripura', 'Sikkim'],
  '80': ['Bihar'],
  '81': ['Bihar', 'Jharkhand'],
  '82': ['Jharkhand', 'Bihar'],
  '83': ['Jharkhand'],
  '84': ['Bihar'],
  '85': ['Bihar'],
};

/**
 * Validates whether a 6-digit Indian pincode matches the chosen State.
 */
export function validatePincodeWithState(
  pincode?: string | null,
  stateInput?: string | null
): { isValid: boolean; expectedStateName?: string; error?: string } {
  if (!pincode || typeof pincode !== 'string') return { isValid: true };
  const cleanPin = pincode.trim();
  if (cleanPin.length !== 6 || !/^\d{6}$/.test(cleanPin)) return { isValid: true };
  if (!stateInput || typeof stateInput !== 'string' || !stateInput.trim()) return { isValid: true };

  const prefix = cleanPin.substring(0, 2);
  const allowedStates = PINCODE_PREFIX_STATE_MAP[prefix];
  if (!allowedStates || allowedStates.length === 0) return { isValid: true };

  // Normalize state input
  let normalizedInput = stateInput.trim().toLowerCase();
  normalizedInput = normalizedInput.replace(/[&]/g, 'and').replace(/\s+/g, ' ');

  const isMatched = allowedStates.some(allowed => {
    const normAllowed = allowed.toLowerCase().replace(/[&]/g, 'and').replace(/\s+/g, ' ');
    return normalizedInput.includes(normAllowed) || normAllowed.includes(normalizedInput);
  });

  if (!isMatched) {
    const expected = allowedStates.join(' / ');
    return {
      isValid: false,
      expectedStateName: expected,
      error: `Pincode ${cleanPin} belongs to ${expected}, but ${stateInput} is selected.`,
    };
  }

  return { isValid: true, expectedStateName: allowedStates[0] };
}

/**
 * Base Rate Card for Air Express per kg
 */
export const AIR_EXPRESS_ZONE_RATES: Record<DestinationRegion, number> = {
  NORTH: 37,
  WEST_CENTRAL: 62,
  EAST: 68,
  SOUTH: 68,
  NE: 98,
  JK: 62,
};

/**
 * Parse artwork dimensions from display_size text or size_in_inches
 * Handles:
 * - 3D Sculptures "61.8 x 23.2 x 17 in", "61.8 × 23.2 × 17 cm", "61.8x23.2x17"
 * - Standard 2D "30 x 40 in", "30 × 40", "30x40"
 * - Ranges like "8 - 11.5 in (variable size)" or "8 to 11.5 in"
 * - Single values like "12 in diameter" -> 12x12
 * - CM units conversion -> converted to inches
 */
export function parseArtworkDimensions(input: any): { widthInches: number; heightInches: number; depthInches: number } {
  if (!input) return { widthInches: 0, heightInches: 0, depthInches: 0 };

  let rawStr = '';
  if (typeof input === 'object') {
    rawStr = String(input.display_size || input.size_in_inches || '').trim();
  } else {
    rawStr = String(input).trim();
  }

  if (!rawStr) return { widthInches: 0, heightInches: 0, depthInches: 0 };

  const str = rawStr.toLowerCase();
  const isCm = str.includes('cm') && !str.includes('in');

  // 1. Check for 3D dimensions (Sculptures: W x H x D / L x W x H)
  const tripleMatch = str.match(
    /([0-9]+(?:\.[0-9]+)?)\s*(?:in|inch|inches|cm)?\s*[xX×*]\s*([0-9]+(?:\.[0-9]+)?)\s*(?:in|inch|inches|cm)?\s*[xX×*]\s*([0-9]+(?:\.[0-9]+)?)/
  );
  if (tripleMatch) {
    let w = parseFloat(tripleMatch[1]);
    let h = parseFloat(tripleMatch[2]);
    let d = parseFloat(tripleMatch[3]);
    if (isCm) {
      w = Number((w / 2.54).toFixed(2));
      h = Number((h / 2.54).toFixed(2));
      d = Number((d / 2.54).toFixed(2));
    }
    return { widthInches: w, heightInches: h, depthInches: d };
  }

  // 2. Check for standard 2D "W x H" (e.g. "30 x 40 in", "30 × 40", "30.5 X 40.2")
  const crossMatch = str.match(/([0-9]+(?:\.[0-9]+)?)\s*(?:in|inch|inches|cm)?\s*[xX×*]\s*([0-9]+(?:\.[0-9]+)?)/);
  if (crossMatch) {
    let w = parseFloat(crossMatch[1]);
    let h = parseFloat(crossMatch[2]);
    if (isCm) {
      w = Number((w / 2.54).toFixed(2));
      h = Number((h / 2.54).toFixed(2));
    }
    return { widthInches: w, heightInches: h, depthInches: 0 };
  }

  // 3. Check for ranges like "8 - 11.5 in (variable size)" or "8 to 11.5 in"
  const rangeMatch = str.match(/([0-9]+(?:\.[0-9]+)?)\s*(?:-|to)\s*([0-9]+(?:\.[0-9]+)?)/);
  if (rangeMatch) {
    const num1 = parseFloat(rangeMatch[1]);
    const num2 = parseFloat(rangeMatch[2]);
    let maxDim = Math.max(num1, num2);
    if (isCm) {
      maxDim = Number((maxDim / 2.54).toFixed(2));
    }
    return { widthInches: maxDim, heightInches: maxDim, depthInches: 0 };
  }

  // 4. Check for single dimension like "12 in" or "12 inch diameter"
  const singleMatch = str.match(/([0-9]+(?:\.[0-9]+)?)/);
  if (singleMatch) {
    let dim = parseFloat(singleMatch[1]);
    if (isCm) {
      dim = Number((dim / 2.54).toFixed(2));
    }
    return { widthInches: dim, heightInches: dim, depthInches: 0 };
  }

  return { widthInches: 0, heightInches: 0, depthInches: 0 };
}

/**
 * 1. Calculate Package Dimensions according to Quiet Art Packaging Rules:
 * - Package Width = Artwork Width + 4 inches
 * - Package Height = Artwork Height + 4 inches
 * - Package Depth:
 *     - Sculptures / 3D Artworks: artworkDepth + 8 inches
 *     - 2D Artworks (Non-Frame): 8 inches
 *     - 2D Artworks (Frame): 8 + selectedFrameDepth (or 8 inches if frame depth not provided)
 */
export function calculatePackageDimensions(input: ArtworkPackagingInput): PackageDimensions {
  const artworkWidth = Number(input.artworkWidth) || 0;
  const artworkHeight = Number(input.artworkHeight) || 0;
  const artworkDepth = Number(input.artworkDepth) || 0;
  const hasFrame = Boolean(input.hasFrame);
  const frameDepth = typeof input.selectedFrameDepth === 'number' && input.selectedFrameDepth > 0 ? input.selectedFrameDepth : 0;

  const packageWidthInches = artworkWidth + 4;
  const packageHeightInches = artworkHeight + 4;
  const baseDepth = artworkDepth > 0 ? artworkDepth + 8 : 8;
  const packageDepthInches = hasFrame && frameDepth > 0 ? baseDepth + frameDepth : baseDepth;

  // Convert to Centimeters (1 inch = 2.54 cm)
  const packageWidthCm = Number((packageWidthInches * 2.54).toFixed(2));
  const packageHeightCm = Number((packageHeightInches * 2.54).toFixed(2));
  const packageDepthCm = Number((packageDepthInches * 2.54).toFixed(2));

  // Volumetric Formulas
  // Air / DP: (L * W * H cm) / 5000
  const airVolumetricWeightKg = Number(((packageWidthCm * packageHeightCm * packageDepthCm) / 5000).toFixed(3));

  // Surface: (L * W * H cm) / 4500
  const surfaceVolumetricWeightKg = Number(((packageWidthCm * packageHeightCm * packageDepthCm) / 4500).toFixed(3));

  // Oversize flag: Not applicable
  const isOverSize = false;

  return {
    packageWidthInches,
    packageHeightInches,
    packageDepthInches,
    packageWidthCm,
    packageHeightCm,
    packageDepthCm,
    airVolumetricWeightKg,
    surfaceVolumetricWeightKg,
    isOverSize,
  };
}

/**
 * Helper to resolve region and location traits from State string or pincode
 */
export function resolveLocationTraits(stateName?: string): {
  zone: DestinationRegion;
  isDelhi: boolean;
  isMaharashtra: boolean;
  isRemoteArea: boolean;
} {
  if (!stateName) {
    return {
      zone: 'WEST_CENTRAL',
      isDelhi: false,
      isMaharashtra: false,
      isRemoteArea: false,
    };
  }

  const normalized = stateName.trim().toLowerCase();
  const zone = STATE_REGION_MAP[normalized] || 'WEST_CENTRAL';
  const isDelhi = normalized.includes('delhi');
  const isMaharashtra = normalized.includes('maharashtra');
  const isRemoteArea = RAS_STATES.has(normalized);

  return {
    zone,
    isDelhi,
    isMaharashtra,
    isRemoteArea,
  };
}

/**
 * Comprehensive parser for database weight strings (e.g. "100gm", "10kg", "12,000 g", "12000", "130.7 grams approximately", "14 Kg")
 * Converts all formats reliably to kilograms (kg).
 */
export function parseWeightKg(weightInput: any): number {
  if (typeof weightInput === 'number') {
    if (isNaN(weightInput) || weightInput <= 0) return 0;
    // Numbers > 100 without unit are grams in art catalog (e.g. 12000 = 12kg, 500 = 0.5kg)
    return weightInput > 100 ? Number((weightInput / 1000).toFixed(3)) : Number(weightInput.toFixed(3));
  }
  if (!weightInput) return 0;

  const str = String(weightInput).trim().toLowerCase();
  if (!str) return 0;

  // Extract numeric part (handles commas like "12,000" or "15,000.5")
  const numMatch = str.match(/([0-9]+(?:,[0-9]+)*(?:\.[0-9]+)?)/);
  if (!numMatch) return 0;

  const rawNumStr = numMatch[1].replace(/,/g, '');
  const val = parseFloat(rawNumStr);
  if (isNaN(val) || val <= 0) return 0;

  // Check unit in the string
  const isKg = /\b(kg|kgs|kilogram|kilograms)\b/i.test(str) || str.includes('kg');
  const isGram = /\b(gm|gms|g|gram|grams)\b/i.test(str) || str.includes('gm') || str.includes('gram');

  if (isKg) {
    return Number(val.toFixed(3));
  }

  if (isGram) {
    return Number((val / 1000).toFixed(3));
  }

  // If no unit is specified:
  // Values > 100 (e.g. 12000, 14000, 500) are in grams
  if (val > 100) {
    return Number((val / 1000).toFixed(3));
  }

  // Otherwise values <= 100 (e.g. 10, 14, 2.5) are in kg
  return Number(val.toFixed(3));
}

/**
 * 2. Calculate Complete Shipping Cost & Surcharges
 */
export function calculateShippingCost(
  input: ArtworkPackagingInput,
  options: ShippingCostOptions = {}
): ShippingCalculationResult {
  const packageDimensions = calculatePackageDimensions(input);
  const actualWeightKg = parseWeightKg(input.actualWeightKg);
  const declaredValueInr = Number(input.declaredValueInr) || 0;

  // Shipping Mode
  const mode = options.mode || 'Air';
  const volumetricWeightKg = mode === 'Surface' ? packageDimensions.surfaceVolumetricWeightKg : packageDimensions.airVolumetricWeightKg;

  // Chargeable weight is based purely on volumetric weight
  const chargeableWeightKg = volumetricWeightKg;

  // For Air Express: Minimum chargeable weight is 10 kg
  const minChargeableWeightKg = mode === 'Air' ? 10 : 1;
  const billableWeightKg = Math.max(chargeableWeightKg, minChargeableWeightKg);

  // Resolve location traits if state provided
  const locationTraits = resolveLocationTraits(options.destinationState);
  const destinationZone = options.destinationZone || locationTraits.zone;
  const isDelhi = options.isDelhi ?? locationTraits.isDelhi;
  const isMaharashtra = options.isMaharashtra ?? locationTraits.isMaharashtra;
  const isRemoteArea = options.isRemoteArea ?? locationTraits.isRemoteArea;

  // Rates & Minimums
  const ratePerKg = AIR_EXPRESS_ZONE_RATES[destinationZone] ?? 62;
  const minBaseFreight = 280;
  const rawBaseFreight = billableWeightKg * ratePerKg;
  const baseFreight = Number(Math.max(rawBaseFreight, minBaseFreight).toFixed(2));

  // 1. Docket / AWB Fee
  const docketFee = typeof options.docketFee === 'number' ? options.docketFee : 70;

  // 2. FOV (Freight on Value / Owner's Risk)
  // Air Express: 0.1% or INR 100 whichever is higher
  const fovFee = Number(Math.max(100, declaredValueInr * 0.001).toFixed(2));

  // 3. OS/OW (Over Size / Over Weight) - Removed
  const osOwFee = 0;

  // 4. ECC (Environment Compensation Charge - Delhi shipments)
  let eccFee = 0;
  if (isDelhi) {
    if (billableWeightKg <= 50) eccFee = 80;
    else if (billableWeightKg <= 100) eccFee = 130;
    else eccFee = 175;
  }

  // 5. Special Handling (Maharashtra deliveries: Rs 3/kg)
  const specialHandlingFee = isMaharashtra ? Number((billableWeightKg * 3).toFixed(2)) : 0;

  // 6. RAS (Remote Area Surcharge: Rs 3/kg)
  const remoteAreaSurcharge = isRemoteArea ? Number((billableWeightKg * 3).toFixed(2)) : 0;

  // 7. VCHC (Valuable Cargo Handling Charge: If declared value > 50,000/kg)
  let vchcFee = 0;
  if (declaredValueInr > billableWeightKg * 50000) {
    vchcFee = Number(Math.max(500, declaredValueInr * 0.005).toFixed(2));
  }

  // Subtotal of fixed and direct charges
  const subtotal = Number(
    (
      baseFreight +
      docketFee +
      fovFee +
      osOwFee +
      eccFee +
      specialHandlingFee +
      remoteAreaSurcharge +
      vchcFee
    ).toFixed(2)
  );

  // Compounded percentage surcharges
  // Fuel Surcharge (FS): 81.60% of Subtotal
  const fsPercent = typeof options.fuelSurchargePercent === 'number' ? options.fuelSurchargePercent : 81.6;
  const fuelSurcharge = Number(((subtotal * fsPercent) / 100).toFixed(2));

  // CAF (Currency Adjustment Factor): 17.50% of (Subtotal + FS)
  const cafPercent = typeof options.cafPercent === 'number' ? options.cafPercent : 17.5;
  const caf = Number((((subtotal + fuelSurcharge) * cafPercent) / 100).toFixed(2));

  // IDC (Infrastructure Development Charges): 5% of (Subtotal + FS + CAF)
  const idcPercent = typeof options.idcPercent === 'number' ? options.idcPercent : 5.0;
  const idc = Number((((subtotal + fuelSurcharge + caf) * idcPercent) / 100).toFixed(2));

  // Total Freight / Shipping Cost
  const totalShippingCost = Number((subtotal + fuelSurcharge + caf + idc).toFixed(2));

  return {
    packageDimensions,
    actualWeightKg,
    volumetricWeightKg: Number(volumetricWeightKg.toFixed(2)),
    chargeableWeightKg: Number(chargeableWeightKg.toFixed(2)),
    billableWeightKg: Number(billableWeightKg.toFixed(2)),
    breakdown: {
      ratePerKg,
      minChargeableWeightKg,
      minBaseFreight,
      baseFreight,
      docketFee,
      fovFee,
      osOwFee,
      eccFee,
      specialHandlingFee,
      remoteAreaSurcharge,
      vchcFee,
      subtotal,
      fuelSurcharge,
      caf,
      idc,
    },
    totalShippingCost,
  };
}
